import { Fragment, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Headline from '../../../../components/Headline';
import Description from '../../../../components/Description';
import Button from '../../../../components/Button';
import List from '../../../../components/List';
import FormAndCalculatorStylingModal from '../../../../../../Modal/FormAndCalculatorStylingModal';
import { getBrokerProductSummary } from '../../../../../../../services/integrationResources';
import { selectUserInfo } from '../../../../../../../store/selectors';
import { selectBrokerProduct } from '../../../../../../../store/selectors/brokerProducts';
import Notification from '../../../../../../../components/Utils/Notification';

const FormCalculatorStyling = () => {
  const [showModal, setShowModal] = useState(false);
  const { recommendationCode } = useSelector(selectUserInfo)?.data || {};
  const { search } = useLocation();
  const brokerProductStyles = useSelector(selectBrokerProduct)?.data?.data?.styles;
  const hasFormStyles = brokerProductStyles.some((style) => style.type === 'form');
  const hasCalculatorStyles = brokerProductStyles.some((style) => style.type === 'calculator');

  const useQuery = useCallback(() => new URLSearchParams(search), [search]);
  const query = useQuery();
  const product = query.get('product');

  const isProductionHostname = window.location.hostname === 'pragmago.pl';

  const setJsLinkBasedOnHostname = useCallback(() => {
    if (isProductionHostname) {
      return 'https://business.pragmago.pl';
    }
    return 'https://partners-loanfront.box.pragmago.tech';
  }, []);

  const getSelectedProductDetails = useCallback(async () => {
    try {
      return await getBrokerProductSummary(recommendationCode, product);
    } catch (error) {
      console.log(error);
    }
  }, [recommendationCode, product]);

  const contentCalculator = useCallback(
    (styleId) => `const { initializePragmaPayCalculator } = new PragmaGo();
    initializePragmaPayCalculator({
      styleId: '${styleId}',
      recommendationCode: '${recommendationCode}',
      calculator: {
        amount: 0,
        currency: 'PLN'
      }
    });`,
    []
  );

  const contentForm = useCallback(
    (styleId) => `
    const { initializePragmaPay } = new PragmaGo();
    const paymentId = "";  // Payment ID from your backend

    initializePragmaPay({ 
      styleId: '${styleId}',
      paymentId: paymentId,
      isPopup: true,
    })`,
    []
  );

  const codeContent = useCallback(
    (isForm, styleId) => `<script type="module" src="${setJsLinkBasedOnHostname()}/Main.js" async></script>
    <script type="module">
       import { PragmaGo } from "${setJsLinkBasedOnHostname()}/sdk.js";
      ${isForm ? contentForm(styleId) : contentCalculator(styleId)}
    </script>`,
    []
  );

  const handleCodeDownload = useCallback(
    async (type, filename) => {
      try {
        const {
          data: { styles },
        } = await getSelectedProductDetails();
        const { styleId } = styles.find((style) => style.type === type);
        const isForm = type === 'form';

        const blob = new Blob([codeContent(isForm, styleId)], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        Notification({
          type: 'error',
          text: this.props.intl.formatMessage({
            id: 'Something went wrong, try again later',
          }),
        }).show();
      }
    },
    [getSelectedProductDetails]
  );

  const handleFormCodeDownload = useCallback(() => {
    handleCodeDownload('form', 'kod_formularza.txt');
  }, [handleCodeDownload]);

  const handleCalculatorCodeDownload = useCallback(() => {
    handleCodeDownload('calculator', 'kod_kalkulatora.txt');
  }, [handleCodeDownload]);

  const handleModalState = useCallback(() => {
    setShowModal((prev) => !prev);
  }, []);

  return (
    <Fragment>
      <Headline message='Integration form and calculator styling headline' />
      <Description message='Integration form and calculator styling description' />
      <List>
        <Button type='secondary' icon='cog' label='Integration style link' onCallback={handleModalState} />
        <div style={{ width: 12 }} />
        <Button
          type='secondary'
          icon='download'
          label='Integration form code'
          onCallback={handleFormCodeDownload}
          isDisabled={!hasFormStyles}
        />
        <div style={{ width: 12 }} />
        <Button
          type='secondary'
          icon='download'
          label='Integration calculator code'
          onCallback={handleCalculatorCodeDownload}
          isDisabled={!hasCalculatorStyles}
        />
      </List>
      <FormAndCalculatorStylingModal isOpen={showModal} onRequestClose={handleModalState} />
    </Fragment>
  );
};

export default FormCalculatorStyling;
